"use client";

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from './Button';
import { captureFocus, lockDocumentScroll, trapTabKey, unlockDocumentScroll } from './overlayBehavior';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Stable owning-workspace target when the original opener no longer exists. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  ariaLabel?: string;
  role?: 'dialog' | 'alertdialog';
  layer?: 'default' | 'above-search';
}

const Modal: React.FC<ModalProps> = ({
  isOpen, onClose, title, children, size = 'md', showCloseButton = true,
  closeOnOverlayClick = true, initialFocusRef, returnFocusRef, ariaLabel = 'Dialog', role = 'dialog', layer = 'default',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    lockDocumentScroll();
    const releaseFocus = captureFocus(dialogRef.current, initialFocusRef?.current,
      returnFocusRef ? () => returnFocusRef.current : undefined);
    return () => {
      releaseFocus();
      unlockDocumentScroll();
    };
  }, [initialFocusRef, isOpen, returnFocusRef]);

  if (!isOpen) return null;
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'w-[calc(100vw-2rem)] max-w-6xl max-h-[calc(100vh-2rem)]',
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Portals still bubble through React parents, including another Modal.
      event.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(event, dialogRef.current);
  };

  return createPortal(
    <div className={`ss-overlay-backdrop fixed inset-0 flex items-center justify-center ${layer === 'above-search' ? 'z-[calc(var(--ss-layer-search)+10)]' : 'z-overlay'}`} data-ss-overlay-kind="modal" onMouseDown={(event) => { if (closeOnOverlayClick && event.target === event.currentTarget) onClose(); }}>
      <div ref={dialogRef} role={role} aria-modal="true" aria-labelledby={title ? titleId : undefined} aria-label={title ? undefined : ariaLabel} tabIndex={-1} onKeyDown={handleKeyDown} data-ss-overlay-material="transient" data-ss-overlay-scroll-region className={`ss-overlay-panel relative mx-auto w-full ${sizeClasses[size]} overflow-y-auto rounded-feature border shadow-e3`}>
        {(title || showCloseButton) ? <div className="ss-overlay-divider flex items-center justify-between gap-4 border-b p-4 sm:p-6">{title ? <h2 id={titleId} className="text-base font-semibold text-content sm:text-xl">{title}</h2> : <span />}{showCloseButton ? <IconButton label="Close dialog" variant="quiet" onClick={onClose}><XMarkIcon className="h-5 w-5" /></IconButton> : null}</div> : null}
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
    ,
    document.body,
  );
};

export default Modal;
